import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Download, Loader, Calendar as CalendarIcon } from "lucide-react";
import { type DateRange } from "react-day-picker";
import { fetchApi } from "@/lib/api";
import { toast } from "sonner";
import {
  CreateCsvExportResponseSchema,
  GetCsvExportResponseSchema,
} from "@forgepoint/shared";

const RANGE_PRESETS = [
  { value: "last30", label: "Last 30 days" },
  { value: "last90", label: "Last 90 days" },
  { value: "ytd", label: "Year to date" },
  { value: "custom", label: "Custom range" },
] as const;

type RangePreset = (typeof RANGE_PRESETS)[number]["value"];

const POLLING_INTERVAL_MS = 2000;

/**
 * Computes a date range for a given preset.
 * @param preset - The range preset to compute
 * @returns A DateRange with from/to dates
 */
const getPresetRange = (preset: RangePreset): DateRange => {
  const end = new Date();
  const start = new Date();
  if (preset === "last90") {
    start.setUTCDate(start.getUTCDate() - 89);
  } else if (preset === "ytd") {
    start.setUTCMonth(0, 1);
  } else if (preset === "custom") {
    return { from: start, to: end };
  } else {
    start.setUTCDate(start.getUTCDate() - 29);
  }
  start.setUTCHours(0, 0, 0, 0);
  end.setUTCHours(23, 59, 59, 999);
  return { from: start, to: end };
};

/**
 * Formats a date range into a human-readable label.
 * @param range - The date range to format
 * @returns Formatted string like "Jan 1, 2025 – Mar 20, 2025"
 */
const formatRangeLabel = (range?: DateRange): string => {
  if (!range?.from || !range.to) {
    return "Select dates";
  }
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  return `${fmt(range.from)} – ${fmt(range.to)}`;
};

/**
 * Triggers a browser file download from a URL.
 * @param url - The download URL
 * @param fileName - The file name for the download
 */
const triggerDownload = (url: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Popover dialog for exporting quote lines as CSV with a date range picker.
 * Supports preset ranges and custom calendar selection. Initiates an async
 * export, polls for completion, and triggers a browser download.
 */
export function QuoteCsvExportDialog() {
  const [open, setOpen] = useState(false);
  const [rangePreset, setRangePreset] = useState<RangePreset>("last30");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    getPresetRange("last30"),
  );
  const [isExporting, setIsExporting] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [includeCustomFields, setIncludeCustomFields] = useState(true);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  useEffect(() => {
    return () => stopPolling();
  }, []);

  const startPolling = (id: string) => {
    pollingRef.current = setInterval(async () => {
      const response = await fetchApi(`exports/${id}`, "GET");
      if (!response.ok) {
        stopPolling();
        setIsExporting(false);
        toast.error("Failed to check export status");
        return;
      }

      const parsed = GetCsvExportResponseSchema.safeParse(response.data);
      if (!parsed.success) {
        stopPolling();
        setIsExporting(false);
        toast.error("Unexpected response from server");
        return;
      }

      const exportData = parsed.data.export;

      if (exportData.status === "COMPLETE" && exportData.downloadUrl) {
        stopPolling();
        triggerDownload(
          exportData.downloadUrl,
          exportData.fileName ?? "export.csv",
        );
        setIsExporting(false);
        setOpen(false);
        toast.success("Export downloaded");
      } else if (exportData.status === "FAILED") {
        stopPolling();
        setIsExporting(false);
        toast.error(exportData.errorMessage ?? "Export failed");
      }
    }, POLLING_INTERVAL_MS);
  };

  /**
   * Initiates the CSV export by calling the backend API.
   * On cache hit, downloads immediately; otherwise starts polling.
   */
  const handleExport = async () => {
    if (!dateRange?.from || !dateRange.to) {
      return;
    }
    setIsExporting(true);

    const startDate = new Date(dateRange.from);
    const endDate = new Date(dateRange.to);
    startDate.setUTCHours(0, 0, 0, 0);
    endDate.setUTCHours(23, 59, 59, 999);

    const response = await fetchApi("exports/quote-lines-csv", "POST", {
      body: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        sendEmail,
        includeCustomFields,
      },
    });

    if (!response.ok) {
      toast.error("Failed to start export");
      setIsExporting(false);
      return;
    }

    const parsed = CreateCsvExportResponseSchema.safeParse(response.data);
    if (!parsed.success) {
      toast.error("Unexpected response from server");
      setIsExporting(false);
      return;
    }

    const exportData = parsed.data.export;

    if (exportData.status === "COMPLETE" && exportData.downloadUrl) {
      triggerDownload(
        exportData.downloadUrl,
        exportData.fileName ?? "export.csv",
      );
      setIsExporting(false);
      setOpen(false);
      toast.success("Export downloaded");
      return;
    }

    startPolling(exportData.id);
  };

  const handlePresetChange = (value: RangePreset) => {
    setRangePreset(value);
    setDateRange(getPresetRange(value));
  };

  const hasValidRange = dateRange?.from && dateRange.to;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-1" />
          Export CSV
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="end">
        <div className="flex flex-col gap-3">
          <Select value={rangePreset} onValueChange={handlePresetChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select range" />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarIcon className="h-4 w-4" />
            {formatRangeLabel(dateRange)}
          </div>

          {rangePreset === "custom" && (
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
            />
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-custom-fields"
                checked={includeCustomFields}
                onCheckedChange={(checked) =>
                  setIncludeCustomFields(checked === true)
                }
              />
              <Label
                htmlFor="include-custom-fields"
                className="text-sm font-normal"
              >
                Include custom fields
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="send-email"
                checked={sendEmail}
                onCheckedChange={(checked) => setSendEmail(checked === true)}
              />
              <Label htmlFor="send-email" className="text-sm font-normal">
                Also email me a copy
              </Label>
            </div>
          </div>

          <Button
            onClick={handleExport}
            disabled={!hasValidRange || isExporting}
            size="sm"
            className="w-full"
          >
            {isExporting ? (
              <Loader className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            Export
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
